import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { useSendNotification } from '@/api/hooks'
import { Send, X } from 'lucide-react'

export interface NotificationRecipient { id: number; name: string }

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400'

// Send a push notification to a specific set of already-picked users (e.g.
// trainers checked in the user list) — no user-search step, since the
// recipients were already chosen before this modal opened.
export function SendNotificationModal({ open, onClose, recipients, onRemoveRecipient, userType = 'TRAINER', onSuccess }: {
  open: boolean
  onClose: () => void
  recipients: NotificationRecipient[]
  onRemoveRecipient: (id: number) => void
  userType?: string
  onSuccess: () => void
}) {
  const [locale, setLocale] = useState('En')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState('')
  const send = useSendNotification()

  const reset = () => { setTitle(''); setDescription(''); setBody(''); setLocale('En'); setError('') }
  const handleClose = () => { reset(); onClose() }

  const handleSend = () => {
    setError('')
    if (!title.trim()) { setError('Notification title is required.'); return }
    if (recipients.length === 0) { setError('No trainers selected.'); return }
    send.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        detail_message: body.trim() || description.trim(),
        user_type: userType,
        user_ids: recipients.map(r => r.id),
        locale,
      },
      {
        onSuccess: () => {
          toast.success(`Notification sent to ${recipients.length} trainer${recipients.length === 1 ? '' : 's'}.`)
          onSuccess()
          handleClose()
        },
        onError: (e) => setError(getApiError(e)),
      },
    )
  }

  return (
    <Modal open={open} onClose={handleClose} title="Send Notification" size="md">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">
            Recipients ({recipients.length})
          </label>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 rounded-lg border border-gray-200 bg-gray-50">
            {recipients.length === 0 ? (
              <span className="text-xs text-gray-400 py-0.5">No trainers selected</span>
            ) : recipients.map(r => (
              <span key={r.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-gray-200 text-xs font-semibold text-gray-700">
                {r.name}
                <button type="button" onClick={() => onRemoveRecipient(r.id)} className="text-gray-400 hover:text-red-500">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">{error}</div>}

        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Notification title" className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Message</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
            placeholder="Short message shown in the notification" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">
            Details <span className="normal-case text-gray-400 font-normal">(optional — shown when the trainer opens it; leave empty to reuse the message above)</span>
          </label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Optional longer detail" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Language</label>
          <select value={locale} onChange={e => setLocale(e.target.value)} className={inputCls}>
            <option value="En">English</option>
            <option value="Ar">Arabic</option>
            <option value="Ur">Urdu</option>
          </select>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button icon={<Send size={14} />} loading={send.isPending} disabled={recipients.length === 0} onClick={handleSend}>
            Send to {recipients.length} Trainer{recipients.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
